import * as React from 'react';
import { t } from '@reos/i18n';
import { Eye, EyeOff } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ProductLogo } from '../components/product-logo.js';
import { useSession } from '../stores/session.js';
import { fetchMe, setActiveOrganization, signIn } from '../lib/api.js';

const loginSchema = z.object({
  email: z.string().email(t('webAdmin.login.emailInvalid', { locale: 'cs' })),
  password: z.string().min(12, t('webAdmin.login.passwordTooShort', { locale: 'cs' })),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const setSession = useSession((s) => s.setSession);
  const [error, setError] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [resetSuccess] = React.useState(
    () => new URLSearchParams(window.location.search).get('reset') === 'success',
  );
  // Kam po prihlaseni: cesta, na kterou uzivatel puvodne sel (router guard ji
  // predal v `?return_to=`), jinak vstupni obrazovka.
  const [returnTo] = React.useState<string>(() => {
    const raw = new URLSearchParams(window.location.search).get('return_to');
    // Jen cesty v teto aplikaci — externi URL by byl otevreny redirect.
    // Krok C2: vychozi cil je `/prehled` (koren domeny patri verejne strance).
    return raw !== null && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/prehled';
  });

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    setError(null);
    try {
      await signIn(data);
      const me = await fetchMe();
      if (!me) {
        setError(t('webAdmin.login.invalidCredentials', { locale: 'cs' }));
        return;
      }
      // role a tenant jdou ze session, ne z konstanty
      if (!me.tenant) {
        setError(t('webAdmin.login.noTenant', { locale: 'cs' }));
        return;
      }
      // Bez aktivni organizace na session konci kazde domenove volani 403
      // (viz lib/api.ts setActiveOrganization) — nastavuje se hned po prihlaseni.
      await setActiveOrganization(me.tenant.id);
      setSession(me.user, me.tenant);
      void navigate({ to: returnTo });
    } catch {
      setError(t('webAdmin.login.invalidCredentials', { locale: 'cs' }));
    }
  });

  return (
    <div className="max-w-md mx-auto mt-12">
      {/*
        Krok BRAND1: na prihlaseni clovek vidi, KAM se prihlasuje.
        Krok BRAND2: uz to nerika textem, ale logem — na prihlaseni je dost
        sirky, takze stoji v 160 px (manual str. 3: minimum 140 px).
      */}
      <ProductLogo className="block mb-6" testId="login-logo" widthPx={160} />
      <h1 className="text-2xl font-bold mb-2">{t('webAdmin.login.title', { locale: 'cs' })}</h1>
      <p className="text-sm text-neutral-600 mb-6">{t('webAdmin.login.subtitle', { locale: 'cs' })}</p>
      {resetSuccess ? (
        <p role="status" data-testid="login-reset-success" className="text-sm text-green-700 mb-4">
          {t('webAdmin.login.resetSuccess', { locale: 'cs' })}
        </p>
      ) : null}
      <form onSubmit={onSubmit} noValidate className="space-y-4" aria-label={t('webAdmin.login.formAriaLabel', { locale: 'cs' })}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-1">{t('webAdmin.login.emailLabel', { locale: 'cs' })}</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            aria-describedby={form.formState.errors.email ? 'email-error' : undefined}
            {...form.register('email')}
            className="w-full border rounded px-3 py-2"
            data-testid="login-email"
          />
          {form.formState.errors.email ? (
            <p id="email-error" role="alert" className="text-sm text-red-600 mt-1">{form.formState.errors.email.message}</p>
          ) : null}
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-1">{t('webAdmin.login.passwordLabel', { locale: 'cs' })}</label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              aria-describedby={form.formState.errors.password ? 'password-error' : undefined}
              {...form.register('password')}
              className="w-full border rounded px-3 py-2 pr-10"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={t(showPassword ? 'webAdmin.login.hidePassword' : 'webAdmin.login.showPassword', { locale: 'cs' })}
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-500 hover:text-neutral-800"
              data-testid="login-password-toggle"
            >
              {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
            </button>
          </div>
          {form.formState.errors.password ? (
            <p id="password-error" role="alert" className="text-sm text-red-600 mt-1">{form.formState.errors.password.message}</p>
          ) : null}
        </div>
        {error ? <p role="alert" className="text-sm text-red-600" data-testid="login-error">{error}</p> : null}
        <button
          type="submit"
          className="w-full bg-neutral-900 text-rh-on-accent rounded px-4 py-2 hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2"
          data-testid="login-submit"
        >
          {t('webAdmin.login.submit', { locale: 'cs' })}
        </button>
        <p className="text-sm text-neutral-600">
          <Link to="/reset-password" data-testid="login-forgot-password" className="underline">
            {t('webAdmin.login.forgotPassword', { locale: 'cs' })}
          </Link>
        </p>
      </form>
    </div>
  );
}
